<!-- BusinessHoursRow.vue -->
<template>
  <div class="field with-icon openingTimes-row">
    <div class="opening-times-inputs">
      <div class="control select-input">
        <n-select
          v-model="row.day"
          :options="options"
          label="Select a day"
          label-prop="name"
          value-prop="name"
          disabled-prop="disabled"
          :hide-label="true"
          @update:model-value="onRowChange"
        />
      </div>
      <div class="toggle-container">
        <div class="control">
          <n-toggle v-model="row.open" label="Toggle Online/Offline" :hide-label="true" @input="onRowChange" />
        </div>
        <div class="control inline-item toggle-label">
          <span class="toggle-label__text--online" v-if="row.open">online</span>
          <span class="toggle-label__text--offline" v-if="!row.open">offline</span>
        </div>
      </div>
      <div class="time-inputs">
        <div class="control time-input" v-show="row.open">
          <n-text-input
            type="text"
            v-model="row.startTime"
            label="Start time"
            :hide-label="true"
            :error-message="getErrorMessage('startTime', 'Start time')"
            @input="onRowChange"
          />
        </div>
        <span class="control inline-item" v-if="row.open">to</span>
        <div class="control time-input" v-show="row.open">
          <n-text-input
            type="text"
            v-model="row.endTime"
            label="End time"
            :hide-label="true"
            :error-message="getErrorMessage('endTime', 'End time')"
            @input="onRowChange"
          />
        </div>
      </div>
    </div>
    <div class="inline-item delete-icon">
      <svg @click="deleteRow" class="add-icon" v-if="showDeleteIcon">
        <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#trash" />
      </svg>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import useVuelidate, { ValidationRule } from '@vuelidate/core';
import { helpers, requiredIf } from '@vuelidate/validators';
import { BusinessHoursOption, BusinessHoursOptions } from '.';
import { HolidayViewModel } from '@/features/bots/models/holidayViewModel';
import DateTimeService from '@shared/services/dateTime';
import VuelidateUtil from '@shared/util/vuelidateUtil';

// Props
const props = defineProps<{
  modelValue: BusinessHoursOption;
  options: any[]; // Adjust type if needed
  showDeleteIcon: boolean;
  holidays: HolidayViewModel[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: BusinessHoursOption): void;
  (e: 'delete'): void;
}>();

// Two-way binding for the row
const row = ref(props.modelValue);
watch(
  () => props.modelValue,
  (newVal: BusinessHoursOptions) => {
    row.value = newVal;
  },
);
watch(row, () => emit('update:modelValue', row.value), { deep: true });

// Validation rules
const time = (value: string) => !row.value.open || !value || DateTimeService.isValidTime(value);
const after = (prop: keyof BusinessHoursOption) =>
  helpers.withMessage(`End time must be after ${prop}`, (value: string) => {
    if (!value || !row.value.open) return true;
    const compareTo = row.value[prop];
    if (!compareTo || typeof compareTo !== 'string') return true;
    const minutes = DateTimeService.getMinutes(value);
    const compareToMinutes = DateTimeService.getMinutes(compareTo)!;
    return minutes !== undefined && minutes > compareToMinutes;
  });

const rules = {
  startTime: {
    required: requiredIf(() => row.value.open),
    time,
  },
  endTime: {
    required: requiredIf(() => row.value.open),
    time,
    after: after('startTime'),
  },
};

const $v = useVuelidate(rules, row);

// Error message generation
function getErrorMessage(field: 'startTime' | 'endTime', label: string): string | null {
  return VuelidateUtil.getErrorMessage($v, field, label, (validationRule: ValidationRule) => {
    if (field === 'endTime' && VuelidateUtil.isValidationInvalid($v, field, 'after')) return 'Must be after start time';
    if (VuelidateUtil.isValidationInvalid($v, field, 'time')) return 'Must be in HH:mm format';
    return '';
  });
}

// Handlers
function onRowChange() {
  $v.value.$touch();
  emit('update:modelValue', row.value);
}

function deleteRow() {
  emit('delete');
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.selectSeparator {
  font-size: 1rem;
  color: #ccc;
}

.openingTimes-row {
  display: flex;
  .opening-times-inputs {
    display: flex;
    flex: 1 1 auto;
    align-items: flex-start;
    min-width: 0;
  }
  .control {
    &:not(:last-child) {
      margin-right: 0.5em;
    }
    &.select-input {
      flex: 0 0 30%;
      @media screen and (min-width: 1600px) {
        flex: 0 0 35%;
      }
    }
  }

  .toggle-container {
    display: flex;
    align-items: center;
    margin: 0 1.5rem 0 1rem;
    .control {
      margin-right: 0;
    }
    .toggle-label {
      &__text {
        &--online {
          display: none;
          @media screen and (min-width: 1600px) {
            display: inline;
          }
        }
      }
    }
  }

  .form-field {
    margin-bottom: 0px;
    &:not(:first-of-type) {
      padding-left: 0.5em;
    }
  }

  .time-inputs {
    display: flex;
    align-items: flex-start;
  }
  .time-input {
    max-width: 65px;
  }
}

.timezone {
  margin-bottom: 12px;

  .timezone-label {
    opacity: 0.6;
    margin-right: 0.25rem;
  }

  .timezone-text {
    opacity: 0.8;
  }
}

.with-icon {
  .add-icon {
    width: 1.25em;
    height: 1.25em;
    fill: #c7cdd3;
    margin-left: 0.5em;
    cursor: pointer;

    &:hover {
      fill: #a4a6a9;
    }
  }

  &.disabled {
    .add-icon {
      cursor: default;
      fill-opacity: 0.5;

      &:hover {
        fill: #c7cdd3;
      }
    }
  }
}

.inline-item {
  display: flex;
  align-items: center;
  height: 2.25rem;
}

.error-message {
  color: #ff3860;
  font-size: 0.75rem;
  margin-bottom: 0.75rem;
}
</style>
